/**
 * Screen Composition Planner phases (A–E) — mirrors backend stage ids.
 */

export const SCREEN_COMPOSITION_STAGE_ORDER = ['prep', 'C', 'A', 'B', 'D', 'E', 'done'];

export const SCREEN_COMPOSITION_STAGES = [
  {
    id: 'prep',
    letter: '0',
    label: 'Prep clips',
    hint: 'Download media · pause cut · B-roll assets',
    steps: [
      { id: 'download', label: 'Download clip media' },
      { id: 'pauses', label: 'Detect / cut pauses' },
      { id: 'assets', label: 'Prepare B-roll assets' },
    ],
  },
  {
    id: 'C',
    letter: 'C',
    label: 'Speech + vision',
    hint: 'VAD keyframes · face / person · TemporalStabilizer',
    steps: [
      { id: 'vad', label: 'Speech segments / VAD' },
      { id: 'detect', label: 'Face / person detect' },
      { id: 'stabilize', label: 'Stabilize focus timeline' },
    ],
  },
  {
    id: 'A',
    letter: 'A',
    label: 'Contract + lock',
    hint: 'Schema · heuristics · lock composition',
    steps: [
      { id: 'schema', label: 'Load composition schema' },
      { id: 'heuristics', label: 'Build from talk focus / B-roll' },
      { id: 'lock', label: 'Lock draft plan' },
    ],
  },
  {
    id: 'B',
    letter: 'B',
    label: 'Layout candidates',
    hint: 'Generate · crop · score · collide · replan',
    steps: [
      { id: 'candidates', label: 'Generate layout candidates' },
      { id: 'crop_score', label: 'Per-candidate crop + score' },
      { id: 'replan', label: 'Replan invalid layouts' },
    ],
  },
  {
    id: 'D',
    letter: 'D',
    label: 'Multi-format encode',
    hint: 'Apply locks · compose each export target',
    steps: [
      { id: 'apply', label: 'Apply locked layout to clips' },
      { id: 'encode', label: 'Compose & encode format' },
      { id: 'concat', label: 'Concatenate final reel' },
    ],
  },
  {
    id: 'E',
    letter: 'E',
    label: 'Final QA',
    hint: 'Sample frames · collision check · replan once',
    steps: [
      { id: 'sample', label: 'Sample rendered frames' },
      { id: 'collide', label: 'Check face vs captions / graphics' },
      { id: 'reencode', label: 'Replan + re-encode if needed' },
    ],
  },
  {
    id: 'done',
    letter: '✓',
    label: 'Ready',
    hint: 'Export complete',
    steps: [{ id: 'ready', label: 'Final video ready' }],
  },
];

export function compositionStageFromJob(job) {
  const stage = job?.composition_stage;
  if (stage && typeof stage === 'object' && stage.phase) {
    return {
      phase: String(stage.phase),
      step: String(stage.step || ''),
      stepLabel: String(stage.step_label || stage.step || ''),
      phaseLabel: String(stage.phase_label || ''),
      detail: String(stage.detail || job?.message || ''),
      format: stage.format || null,
      formatsDone: Array.isArray(stage.formats_done) ? stage.formats_done : [],
      formatsTotal: Number(stage.formats_total) || 0,
    };
  }
  // Infer from message while older jobs are mid-flight.
  const msg = String(job?.message || '').toLowerCase();
  const status = String(job?.status || '').toLowerCase();
  if (status === 'completed') {
    return { phase: 'done', step: 'ready', stepLabel: 'Final video ready', detail: job?.message || '', format: null, formatsDone: [], formatsTotal: 0 };
  }
  if (msg.includes('qa') || msg.includes('replan')) {
    return { phase: 'E', step: msg.includes('re-encod') ? 'reencode' : 'sample', stepLabel: 'Final QA', detail: job?.message || '', format: null, formatsDone: [], formatsTotal: 0 };
  }
  if (msg.includes('encoding') || msg.includes('composing') || msg.includes('concatenat') || msg.includes('format')) {
    return { phase: 'D', step: msg.includes('concat') ? 'concat' : 'encode', stepLabel: 'Encode', detail: job?.message || '', format: null, formatsDone: [], formatsTotal: 0 };
  }
  if (msg.includes('planning screen') || msg.includes('composition')) {
    return { phase: 'B', step: 'candidates', stepLabel: 'Planning compositions', detail: job?.message || '', format: null, formatsDone: [], formatsTotal: 0 };
  }
  if (msg.includes('speech focus') || msg.includes('face detect')) {
    return { phase: 'C', step: 'detect', stepLabel: 'Speech + vision', detail: job?.message || '', format: null, formatsDone: [], formatsTotal: 0 };
  }
  if (msg.includes('prepar') || msg.includes('pause') || msg.includes('clip')) {
    return { phase: 'prep', step: 'download', stepLabel: 'Prep clips', detail: job?.message || '', format: null, formatsDone: [], formatsTotal: 0 };
  }
  if (['pending', 'running'].includes(status)) {
    return { phase: 'prep', step: 'download', stepLabel: 'Starting…', detail: job?.message || '', format: null, formatsDone: [], formatsTotal: 0 };
  }
  return null;
}

export function stageIndex(phaseId) {
  const idx = SCREEN_COMPOSITION_STAGE_ORDER.indexOf(phaseId);
  return idx < 0 ? 0 : idx;
}
